"use client";

import { useEffect, useRef, useState } from "react";
import { QuestionCard } from "@/components/teste/TestRunner";
import { trackEvent } from "@/lib/analytics";
import {
  personalitateConfig,
  type FactorScores,
} from "@/data/tests/personalitate";
import { submitPersonalitateTest } from "./actions";
import ProfileResult from "./ProfileResult";

/**
 * The Mini-IPIP in the same question card as every other test.
 *
 * It does not go through `TestRunner` itself: that runner's result, analytics,
 * email capture and therapist CTA are all keyed on one score and a category,
 * and this test has five scores and no categories. The question flow (auto
 * advance, cancellable on „Înapoi”, „Mai departe” after stepping back) is the
 * same, and the card is the shared `QuestionCard`.
 */

const ADVANCE_MS = 260;

export default function PersonalitateRunner() {
  const config = personalitateConfig;
  const total = config.questions.length;

  const [phase, setPhase] = useState<"test" | "result">("test");
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(() =>
    Array(total).fill(null),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scores, setScores] = useState<FactorScores | null>(null);
  const [started, setStarted] = useState(false);
  const topRef = useRef<HTMLDivElement | null>(null);
  const advanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const question = config.questions[step];
  const isLast = step === total - 1;
  const allAnswered = answers.every((a) => a !== null);

  const cancelAdvance = () => {
    if (advanceRef.current) {
      clearTimeout(advanceRef.current);
      advanceRef.current = null;
    }
  };

  useEffect(() => cancelAdvance, []);

  const scrollToTop = () =>
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );

  const pick = (value: number) => {
    if (!started) {
      setStarted(true);
      trackEvent("test_personalitate_started");
    }
    setAnswers((prev) => {
      const next = [...prev];
      next[step] = value;
      return next;
    });
    cancelAdvance();
    if (!isLast) {
      advanceRef.current = setTimeout(() => {
        advanceRef.current = null;
        setStep((s) => Math.min(s + 1, total - 1));
      }, ADVANCE_MS);
    }
  };

  const goBack = () => {
    cancelAdvance();
    setStep((s) => Math.max(0, s - 1));
  };

  const goNext = () => {
    cancelAdvance();
    setStep((s) => Math.min(s + 1, total - 1));
  };

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setError(null);
    setSubmitting(true);
    const res = await submitPersonalitateTest(answers as number[]);
    setSubmitting(false);
    if (!res.ok || !res.scores) {
      setError(res.error ?? "Nu am putut calcula profilul. Încearcă din nou.");
      return;
    }
    setScores(res.scores);
    setPhase("result");
    trackEvent("test_personalitate_completed", { ...res.scores });
    scrollToTop();
  };

  const retake = () => {
    cancelAdvance();
    setAnswers(Array(total).fill(null));
    setStep(0);
    setScores(null);
    setError(null);
    setPhase("test");
    scrollToTop();
  };

  return (
    <div ref={topRef} className="scroll-mt-24">
      {phase === "test" || !scores ? (
        <QuestionCard
          step={step}
          total={total}
          text={question.text}
          options={config.options}
          selected={answers[step]}
          onPick={pick}
          onBack={goBack}
          onNext={goNext}
          onSubmit={handleSubmit}
          isLast={isLast}
          canSubmit={allAnswered && !submitting}
          submitting={submitting}
          error={error}
          footnote={config.stepperFootnote}
        />
      ) : (
        <ProfileResult scores={scores} onRetake={retake} />
      )}
    </div>
  );
}
